import { Select } from 'antd';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useUser } from '@/hooks/use-user/use-user';
import { CompaniesResponse } from '@/services/company-service/company-service.types';
import { useEffect, useMemo } from 'react';
import styles from './company-select-bh.module.scss';
import { useTranslation } from 'react-i18next';

export type SelectedCompany =
  | {
    id: number;
    name: string;
    time_zone: string;
  }
  | undefined;

type CompanySelectBhProps = {
  value: SelectedCompany;
  onChange: (value: SelectedCompany) => void;
  /** Narrows the companies on offer, and so the one picked by default. Keep it stable. */
  filter?: (company: CompaniesResponse[number]) => boolean;
};

export function CompanySelectBh({ value, onChange, filter }: CompanySelectBhProps) {
  const { t } = useTranslation('settings');
  const { companies: allCompanies } = useCompaniesList();
  const companies = useMemo(
    () => (filter ? allCompanies?.filter(filter) : allCompanies),
    [allCompanies, filter]
  );
  const { userData } = useUser();

  const companyOptions =
    companies?.map((company) => ({
      value: company.id,
      label: company.name,
    })) ?? [];

  useEffect(() => {
    if (!value && userData && companies && companies.length > 0) {
      const isCompanyAdmin = userData.roles?.some((role) => role.name === 'company_admin');

      // Fallback chain, not an either/or: an id that matches nothing used to skip the
      // name lookup entirely, and with the control hidden there is no way back from that.
      const byId =
        isCompanyAdmin && userData.company
          ? companies.find((company) => company.id === userData.company?.id)
          : undefined;
      const byName = userData.default_company
        ? companies.find((company) => company.name === userData.default_company)
        : undefined;
      const targetCompany = byId ?? byName ?? (companies.length === 1 ? companies[0] : undefined);

      if (targetCompany) {
        onChange({ id: targetCompany.id, name: targetCompany.name, time_zone: targetCompany.time_zone });
      }
    }
  }, [value, userData, companies, onChange]);

  // After the effect above, so the single company is still selected for the queries that
  // depend on it: hidden, not unset. Exactly one, so a failed or pending request leaves the
  // control on screen — every tab here gates its body on a company being chosen.
  if (companies?.length === 1) return null;

  const handleChange = (id: number | undefined) => {
    if (!id) {
      onChange(undefined);
      return;
    }
    const company = companies?.find((company) => company.id === id);
    if (company) {
      onChange({ id: company.id, name: company.name, time_zone: company.time_zone });
    }
  };

  return (
    <Select
      aria-label={t('companySelect.placeholder')}
      className={styles.companySelect}
      options={companyOptions}
      value={value?.id}
      showSearch
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      onChange={handleChange}
      placeholder={t('companySelect.placeholder')}
      allowClear
      classNames={{
        popup: {
          root: styles.dropdown,
        },
      }}
    />
  );
}
